// src/pages/misc/onboarding/treeBits.tsx
import React from 'react';

// [SPACING] Shared bubbles for the builder tree (styles live in builder.css). One bubble = icon chip + name under it.
// Icons are hand-drawn 24x24 line SVGs, stroke #A9E0F4, width 1.7, round caps. Sizes: 34px normal, 44px for the org (set in ObNode).
export type Kind = 'org' | 'empresa' | 'proyecto' | 'equipo' | 'asesor' | 'admin';

const ICONS: Record<Kind, string> = {
  org: '<path d="M4 20h16M6 20V9l6-4 6 4v11M10 20v-4h4v4"/><path d="M12 3.2l.8 1.6 1.7.2-1.2 1.2.3 1.7-1.6-.8-1.6.8.3-1.7L9.5 5l1.7-.2z"/>',
  empresa: '<path d="M5 20h14M7 20V7l5-3 5 3v13M10 11h1M13 11h1M10 15h1M13 15h1"/>',
  proyecto: '<path d="M4 20h16M6 20v-8l6-5 6 5v8"/><circle cx="12" cy="12.5" r="1.6"/><path d="M12 14.1V17"/>',
  equipo: '<circle cx="9" cy="9" r="2.6"/><path d="M3.5 19c0-3 2.5-5.4 5.5-5.4s5.5 2.4 5.5 5.4"/><circle cx="16.5" cy="10" r="2.1"/><path d="M15 14.2c2.6.2 5 2.3 5 4.8"/>',
  asesor: '<circle cx="12" cy="8.5" r="3"/><path d="M5.5 19.5c0-3.4 2.9-6 6.5-6s6.5 2.6 6.5 6"/>',
  admin: '<circle cx="10" cy="8.5" r="2.8"/><path d="M4 19.5c0-3.2 2.7-5.7 6-5.7 1 0 1.9.2 2.7.6"/><path d="M17.5 12.4l3 1.1v2.6c0 2-1.3 3.6-3 4.2-1.7-.6-3-2.2-3-4.2v-2.6z"/>',
};

export const Icon: React.FC<{ kind: Kind; size: number }> = ({ kind, size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#A9E0F4" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: ICONS[kind] }} />
);

// [ANIMATION] A single bubble. It pops in on mount (ob-pop in builder.css). Extra `className` values (`sel`, `target`, `faded`) are the
// connect-mode states. `org` = the bigger top bubble. `adorn` = a small extra drawn on the chip. The name is cut with "..." past the node width.
// Clicking stops propagation so it does not cancel connect mode on the page behind it.
export const ObNode: React.FC<{
  kind: Kind;
  name: string;
  org?: boolean;
  empty?: boolean;
  className?: string;
  onClick?: () => void;
  adorn?: React.ReactNode;
}> = ({ kind, name, org, empty, className, onClick, adorn }) => (
  <button type="button" className={`ob-node${org ? ' org' : ''} ${kind}${className ? ` ${className}` : ''}`} onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}>
    <span className="ob-chip">
      <Icon kind={kind} size={org ? 44 : 34} />
      {adorn}
    </span>
    <span className={`ob-nm${empty ? ' empty' : ''}`}>{name}</span>
  </button>
);

// [SPACING] slots = cols x rows. If count fits, all are drawn; otherwise (slots - 1) are drawn and the last slot is a "+N" bubble
// where N = count - (slots - 1). The row width is cols x unit - 18 (unit = bubble width + 18px gap) so it wraps after exactly `cols` bubbles.
/** Draw `count` nodes, or slots-1 plus a +N bubble. Row width is set so it never wraps a surprise third line. */
export const BubbleRow: React.FC<{
  kind: Kind;
  count: number;
  label: string;
  cols: number;
  rows?: number;
  unit: number;
  rowRef?: (el: HTMLDivElement | null) => void;
  nodeClass?: (index: number) => string;
  onNode?: (index: number) => void;
  adorn?: (index: number) => React.ReactNode;
}> = ({ kind, count, label, cols, rows = 2, unit, rowRef, nodeClass, onNode, adorn }) => {
  const slots = cols * rows;
  const shown = count <= slots ? count : slots - 1;
  const extra = count > slots ? count - (slots - 1) : 0;
  return (
    <div className="ob-row" ref={rowRef} style={{ width: cols * unit - 18 }}>
      {Array.from({ length: shown }, (_, i) => (
        <ObNode key={`${kind}-${i}`} kind={kind} name={`${label} ${i + 1}`} className={nodeClass?.(i)} onClick={onNode ? () => onNode(i) : undefined} adorn={adorn?.(i)} />
      ))}
      {extra > 0 && <div className="ob-more">+{extra}</div>}
    </div>
  );
};
